/**
 * AdminAccessPanel — shared admin page state renderer for permission /
 * missing-feature / server-error / offline states.
 *
 * Wave 5-Early follow-up: replaces the generic "Insufficient permissions"
 * screen with copy tailored to the actual failure mode:
 *
 *   forbidden      → the current admin role can't perform the action
 *   not_available  → endpoint returned 404 (feature not wired yet)
 *   server_error   → 5xx from the backend
 *   network        → axios reported no response (offline / DNS failure)
 *
 * The panel is a thin visual shell only — no auth logic, no navigation
 * side-effects. Consumers pass in the kind + optional heading override.
 * The classification comes from `classifyApiError(err)` in api/client.
 *
 * All copy is voice-guardrail safe (no promises of restoration or
 * unlocks; no "coming soon" marketing language).
 */
import { AlertTriangle, Lock, Zap, WifiOff } from "lucide-react";

const KIND_COPY = {
  forbidden: {
    icon: Lock,
    heading: "You don't have permission to view this page.",
    body: "Ask a workspace owner to grant the missing permission, or switch to an account with the required team role.",
  },
  not_available: {
    icon: Zap,
    heading: "This feature isn't available yet.",
    body: "The backend endpoint for this admin surface hasn't been wired. If you expect this feature to be live, contact engineering.",
  },
  server_error: {
    icon: AlertTriangle,
    heading: "Something went wrong.",
    body: "The server returned an error. Try again in a moment.",
  },
  network: {
    icon: WifiOff,
    heading: "You appear to be offline.",
    body: "Check your connection and retry.",
  },
  unknown: {
    icon: AlertTriangle,
    heading: "Couldn't load this page.",
    body: "Try refreshing. If the problem persists, contact support.",
  },
};

export default function AdminAccessPanel({
  kind = "unknown",
  heading,
  body,
  testId = "admin-access-panel",
  detail = null,
}) {
  const meta = KIND_COPY[kind] || KIND_COPY.unknown;
  const Icon = meta.icon;
  return (
    <div
      data-testid={testId}
      data-access-kind={kind}
      className="rounded-[var(--ds-radius-card)] p-8 text-center"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-admin-border)",
      }}
    >
      <Icon
        className="w-5 h-5 mx-auto"
        style={{ color: "var(--ds-color-ink-muted)" }}
        aria-hidden="true"
      />
      <h2
        className="mt-3 text-[15px] font-semibold"
        style={{ color: "var(--ds-color-ink)", fontFamily: "var(--ds-font-display)" }}
      >
        {heading || meta.heading}
      </h2>
      <p
        className="mt-1.5 text-[13px] max-w-md mx-auto leading-snug"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        {body || meta.body}
      </p>
      {detail && (
        <p
          className="mt-3 text-[11px] font-mono"
          style={{ color: "var(--ds-color-ink-subtle)" }}
        >
          {detail}
        </p>
      )}
    </div>
  );
}
